import * as React from "react";
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { IData as OutreachListData } from '../../../store/outreach/outreachList';
import { DopTable, IColumnDefinition } from '../../organisms/DopTable';
import { DetailsButton } from '../../atoms/buttons/DetailsButton';
import { EditButton } from '../../atoms/buttons/EditButton';
import { Medewerker } from "../../../entities/Medewerker";

// All the incoming values
interface IProps {
    title: string;
    data: OutreachListData[];
    loading: boolean;
    hasError: boolean;
    errorDetails: string;
    selectedMedewerker?: Medewerker;
    syncMedewerkerWithRedux?: boolean;
}

// Outgoing functions
interface IDispatch {
    onClickDetails: (id: string) => void;
    onClickEdit: (id: string) => void;
    onRefresh: () => void;
    onChangeMedewerker?: (medewerker?: Medewerker) => void;
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({ }));

// Actual Component
export type AllProps = IProps & IDispatch;
export const OutreachList = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');
    const { t: tDatatypes } = useTranslation('datatypes');

    const columns: IColumnDefinition<OutreachListData>[] = [
        { title: tDatatypes('medewerkersLabel'), 
            align: "left", 
            renderer: (value) => value.medewerkers.join(', '), 
            sorter: (a, b) => a.medewerkers.join(', ').localeCompare(b.medewerkers.join(', ')),
            searchable: true
        },
        { title: tDatatypes('contactDatumLabel'), 
            align: "right", 
            renderer: (value) => value.datum.toLocaleDateString(),
            sorter: (a, b) => a.datum.getTime() - b.datum.getTime(),
            searchable: false // Don't search in dates
        },
        { title: tDatatypes('contactenLabel'), 
            align: "right",
            renderer: (value) => value.contacts,
            sorter: (a, b) => a.contacts - b.contacts,
            searchable: false // Don't search in numbers
        },
        { title: tDatatypes('organisatieLabel'), 
            align: "right", 
            renderer: (value) => value.organisatie,
            sorter: (a, b) => a.organisatie.localeCompare(b.organisatie),
            searchable: true
        },
        { title: tDatatypes('doelgroepenLabel'), 
            align: "right", 
            renderer: (value) => value.doelgroep.join(', '), 
            sorter: (a, b) => a.doelgroep.join(', ').localeCompare(b.doelgroep.join(', ')),
            searchable: true
        },
        { title: tCommon('actions'), 
            align: "right", 
            renderer: (value) => (
                <>
                    <DetailsButton onClick={() => props.onClickDetails(value.id)} />
                    <EditButton onClick={() => props.onClickEdit(value.id)} disabled={value.locked} />
                </>
                ),
            searchable: false // Don't search in action buttons
        },
    ]

    return (<DopTable 
                data={{
                    rows: props.data,
                    columns: columns,
                }}
                toolbar={{
                    title: props.title,
                    onRefresh: props.onRefresh,
                    onChangeMedewerker: props.onChangeMedewerker,
                    selectedMedewerker: props.selectedMedewerker,
                    syncMedewerkerWithRedux: props.syncMedewerkerWithRedux,
                    medewerkerLabel: tDatatypes('medewerkerLabel'),
                    enableBuiltinSearch: true,
                    searchPlaceholder: "Search in outreach data..."
                }}
                loading={props.loading}
                hasError={props.hasError}
                errorDetails={props.errorDetails}
                onClickRow={(row) => props.onClickDetails(row.id)}
            />)
}
